import { BookCsvUploadSchema, ProductCsvUploadSchema } from '@/types/api.zod';
import { z } from 'zod';

/**
 * エンティティタイプごとのフィールド設定
 */
export interface EntityConfig {
  optionalFields: string[];
  numberFields: string[];
  booleanFields: string[];
  schema: z.ZodObject<z.ZodRawShape>;
  crossFieldValidation?: (row: Record<string, unknown>, rowIndex: number) => string[];
}

/**
 * エンティティタイプの設定マップ
 * 新しいアップロードタイプ追加時はここにエントリを追加
 */
// CSV の日時形式（区切りは / または -、時刻・秒は任意）。
// 時刻を省略できる CsvDate（first_publish_at）と時刻必須の CsvDateTime の両方を受ける。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;

const zeroPad = (value: number, length: number): string => String(value).padStart(length, '0');

/**
 * CSV 日時文字列を Date に変換する (YYYY/M/D H:m:s or YYYY-M-D H:m:s)。
 * 形式が違う / 存在しない日付 / 時刻が範囲外の場合は null を返す。
 *
 * スキーマ（`CsvDateTime` / `CsvDate`）の正規表現は桁の形しか見ておらず値の範囲を検証しないため、
 * `2026/13/45 10:30:00` や `2026/01/01 99:99` はスキーマを通過してしまう（レビュー指摘 #556）。
 * 実在判定は「Date.UTC のロールオーバー結果が入力と一致するか」で行う。年月日と時分秒を
 * ISO 文字列 1 本で突き合わせることで、存在しない日付と時刻の範囲外を同時に検出する。
 *
 * 注: 4 桁表記の 0000〜0099 年（例 `0026/06/01 10:30`）は Date.UTC の 2 桁年マッピングにより
 * 1926 年へ変換されるため不一致となり不正扱いになる。Menu / Corner / Special の
 * バリデータも同じ判定方式のため挙動は揃っている。
 *
 * 「形式が違えば null」は本関数の契約の一部だが、呼び出し元へ渡る値はスキーマ検証を
 * 通過済みで必ず形式に合致する。契約として直接テストするため export している。
 */
export const parseCsvDateTime = (value: string): Date | null => {
  const matched = CSV_DATETIME_RE.exec(value.trim());
  if (!matched) return null;

  const [, y, mo, d, h, mi, s] = matched;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h ?? 0);
  const minute = Number(mi ?? 0);
  const second = Number(s ?? 0);

  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const expected =
    `${zeroPad(year, 4)}-${zeroPad(month, 2)}-${zeroPad(day, 2)}` +
    `T${zeroPad(hour, 2)}:${zeroPad(minute, 2)}:${zeroPad(second, 2)}.000Z`;
  return parsed.toISOString() === expected ? parsed : null;
};

// 値の範囲まで検証する CSV 日時列。schemas/models.yaml の CsvDateTime / CsvDate 列と一致させること。
const BOOK_DATETIME_FIELDS = ['first_publish_at', 'release_at', 'publish_at', 'stop_at'] as const;
const PRODUCT_DATETIME_FIELDS = ['release_at', 'publish_at', 'start_at', 'stop_at'] as const;

/**
 * 日時列の「値」を検証する。
 *
 * スキーマは書式のみを検証するため、存在しない日付や範囲外の時刻はここで初めてエラーになる。
 * 以前は前後関係チェックの中で暗黙に無視され、不正な日付の行がそのまま valid として
 * 通っていた（レビュー指摘 #556）。
 */
const validateDateTimeValues = (
  row: Record<string, unknown>,
  fields: readonly string[],
): string[] =>
  fields.flatMap((field) => {
    const raw = row[field];
    if (typeof raw !== 'string' || raw === '') return [];
    return parseCsvDateTime(raw) === null
      ? [`${field}: 日時の値が不正です（存在しない日付、または時刻が範囲外です）: '${raw}'`]
      : [];
  });

/**
 * カンマ区切り文字列の要素数を返す（空文字列は 0）
 */
/** 値がオブジェクト（配列・null を除く）かを判定する型ガード。 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** `isOptional()` を持つスキーマかを確認し、optional かどうかを返す（zod の内部型に依存しない）。 */
const isOptionalSchema = (schema: unknown): boolean =>
  isRecord(schema) && typeof schema.isOptional === 'function' && schema.isOptional() === true;

const countCsvValues = (value: string | undefined): number => {
  if (!value) return 0;
  return value.split(',').filter((s) => s.trim()).length;
};

/**
 * 行の値を「文字列なら文字列、それ以外は undefined」として取り出す。
 *
 * 行は CSV 由来の `Record<string, unknown>` なので、値の型は実行時にしか分からない。
 * `as string | undefined` で黙らせると、数値や配列が混ざった場合に後段の
 * `split` / 比較が実行時エラーになる。AGENTS.md 1（キャスト禁止）に従い型ガードで確定させる。
 */
const readOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/**
 * 著者フィールドのクロスフィールドバリデーション
 * author_name, author_kana, author_title の要素数が一致するか検証
 */
const validateAuthorFields = (row: Record<string, unknown>): string[] => {
  const errors: string[] = [];
  const authorName = readOptionalString(row.author_name);
  const authorKana = readOptionalString(row.author_kana);
  const authorTitle = readOptionalString(row.author_title);

  // 著者名がない場合はスキップ（optional の場合）
  if (!authorName && !authorKana && !authorTitle) return errors;

  const nameCount = countCsvValues(authorName);
  const kanaCount = countCsvValues(authorKana);
  const titleCount = countCsvValues(authorTitle);

  if (!(nameCount === kanaCount && kanaCount === titleCount)) {
    errors.push(
      `author: 著者フィールドの要素数が一致しません（author_name: ${nameCount}件, author_kana: ${kanaCount}件, author_title: ${titleCount}件）`,
    );
  }

  return errors;
};

/**
 * エンティティタイプごとのクロスフィールドバリデーション
 */
type CrossFieldValidator = (row: Record<string, unknown>, rowIndex: number) => string[];

const bookCrossFieldValidation: CrossFieldValidator = (row) => {
  const errors: string[] = validateDateTimeValues(row, BOOK_DATETIME_FIELDS);
  const stopAt = readOptionalString(row.stop_at);
  const releaseAt = readOptionalString(row.release_at);
  if (stopAt && releaseAt) {
    const stopDate = parseCsvDateTime(stopAt);
    const releaseDate = parseCsvDateTime(releaseAt);
    if (stopDate && releaseDate && stopDate <= releaseDate) {
      errors.push('stop_at: 配信停止日は配信開始日より後の日時を指定してください');
    }
  }
  errors.push(...validateAuthorFields(row));
  return errors;
};

const productCrossFieldValidation: CrossFieldValidator = (row) => {
  const errors: string[] = validateDateTimeValues(row, PRODUCT_DATETIME_FIELDS);
  const stopAt = readOptionalString(row.stop_at);
  const startAt = readOptionalString(row.start_at);
  if (stopAt && startAt) {
    const stopDate = parseCsvDateTime(stopAt);
    const startDate = parseCsvDateTime(startAt);
    if (stopDate && startDate && stopDate <= startDate) {
      errors.push('stop_at: 販売停止日は販売開始日より後の日時を指定してください');
    }
  }
  const isCampaign = row.is_campaign;
  const isFreeStreaming = row.is_free_streaming;
  if (isFreeStreaming && !isCampaign) {
    errors.push('is_free_streaming: 無料ストリーミングにはキャンペーンフラグが必要です');
  }
  errors.push(...validateAuthorFields(row));
  return errors;
};

export const ENTITY_CONFIGS: Record<string, EntityConfig> = {
  book: {
    optionalFields: [
      'volume_number_title',
      'is_completed',
      'collection_source_jdcn',
      'isbn',
      'trial_pages',
      'stop_at',
      'search_keywords',
      'jasrac',
      'category',
    ],
    numberFields: ['base_price', 'publish_since', 'trial_pages'],
    booleanFields: ['is_completed'],
    schema: BookCsvUploadSchema,
    crossFieldValidation: bookCrossFieldValidation,
  },
  product: {
    optionalFields: [
      'product_group_title_kana',
      'product_title_kana',
      'product_order',
      'product_order_title',
      'is_completed',
      'file_name',
      'has_collection',
      'collection_source_jdcn',
      'magazine_series_name',
      'magazine_series_title',
      'magazine_name',
      'magazine_title',
      'author_name',
      'author_kana',
      'author_title',
      'publish_since',
      'trial_jdcn',
      'stop_at',
      'is_campaign',
      'is_free_streaming',
      'campaign_type',
      'campaign_value',
      'product_catch',
      'publisher',
      'category',
    ],
    numberFields: ['base_price', 'publish_since', 'product_order'],
    booleanFields: ['is_campaign', 'is_free_streaming', 'is_completed'],
    schema: ProductCsvUploadSchema,
    crossFieldValidation: productCrossFieldValidation,
  },
  // 他のアップロードタイプをここに追加
  // corner: { optionalFields: [...], numberFields: [...], schema: CornerCsvUploadSchema },
};

/**
 * アップロードタイプ → エンティティタイプのマッピング
 */
const UPLOAD_TYPE_MAP: Record<string, string> = {
  Book: 'book',
  Product: 'product',
  // Corner: 'corner',
  // Display: 'display',
};

/**
 * アップロードタイプからエンティティタイプを取得
 */
export const getEntityType = (uploadType: string): string => {
  const entityType = UPLOAD_TYPE_MAP[uploadType];
  if (!entityType) {
    throw new Error(`Unknown upload type: ${uploadType}`);
  }
  return entityType;
};

/**
 * CSVヘッダーバリデーション
 * スキーマから期待されるヘッダーを自動取得し、CSV のヘッダー行と照合する
 */
export const validateCSVHeaders = (
  headers: string[],
  rows: string[][],
  uploadType: string,
): string[] => {
  const entityType = getEntityType(uploadType);
  const config = ENTITY_CONFIGS[entityType];
  // getEntityType が未知のタイプで throw するため、ここで config が無いのは
  // UPLOAD_TYPE_MAP と ENTITY_CONFIGS が食い違ったときだけ（現状は到達しない）。
  /* v8 ignore next -- getEntityType が先に throw するため到達不能 */
  if (!config) return [];

  const allHeaders = Object.keys(config.schema.shape);
  const requiredHeaders = Object.entries(config.schema.shape)
    // `shape` の値型は zod の内部型なので、`isOptional()` を持つことだけを型ガードで確認する
    // （`as z.ZodType` は zod のバージョン差で実際の形が変わっても黙って通ってしまう）。
    .filter(([, fieldSchema]) => !isOptionalSchema(fieldSchema))
    .map(([key]) => key);
  const errors: string[] = [];

  // ヘッダー名の照合
  const headerSet = new Set(headers);
  const allHeaderSet = new Set(allHeaders);

  // 必須列のみ不足チェック（optional フィールドは CSV に無くてもOK）
  const missingHeaders = requiredHeaders.filter((h) => !headerSet.has(h));
  // スキーマに存在しない列を検出
  const unknownHeaders = headers.filter((h) => !allHeaderSet.has(h));

  if (missingHeaders.length > 0) {
    errors.push(`CSVヘッダーに必須列が不足しています: ${missingHeaders.join(', ')}`);
  }
  if (unknownHeaders.length > 0) {
    errors.push(`CSVヘッダーに不明な列があります: ${unknownHeaders.join(', ')}`);
  }

  // ヘッダー自体にエラーがある場合は行チェックをスキップ
  if (errors.length > 0) return errors;

  // データ行の存在チェック（1行目: ヘッダー、2行目: 表示名、3行目以降: データ）
  if (rows.length === 0) {
    errors.push('データ行がありません（3行目以降にデータが必要です）');
    return errors;
  }

  // 各行の列数チェック
  const expectedCount = headers.length;
  const mismatchRows: string[] = [];
  rows.forEach((row, idx) => {
    if (row.length !== expectedCount) {
      mismatchRows.push(`${idx + 3}行目（${row.length}列）`);
    }
  });
  if (mismatchRows.length > 0) {
    const preview = mismatchRows.length > 5 ? mismatchRows.slice(0, 5).join(', ') + ` 他${mismatchRows.length - 5}件` : mismatchRows.join(', ');
    errors.push(`列数が一致しません（期待: ${expectedCount}列）: ${preview}`);
  }

  return errors;
};

// 値として拒否するリテラル（大文字小文字を区別しない完全一致）。
// JS の undefined / null が文字列として CSV に書き出される事故の防波堤
// （実例: magazine_name / magazine_title が "undefined" で保存された）。
// バックエンド（csv_parser.py）でも同一ルールで再検証する。
const INVALID_LITERAL_VALUES: ReadonlySet<string> = new Set(['undefined', 'null']);

const findInvalidLiteralFields = (row: unknown): string[] => {
  if (!isRecord(row)) return [];
  return Object.entries(row)
    .filter(
      ([, value]) => typeof value === 'string' && INVALID_LITERAL_VALUES.has(value.trim().toLowerCase())
    )
    .map(([field]) => field)
    .sort();
};

/**
 * 汎用バリデーション関数
 */
const validateEntity = <T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: z.ZodError } => {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, errors: result.error };
  }
};

/**
 * `field: message` 形式のエラー文字列を列名と本文へ分解する。
 *
 * `formatValidationErrors` / クロスフィールド検証は `${field}: ${message}` で組み立てるが、
 * 型の上では単なる `string` なので、受け取り側は分解結果を確定させる必要がある。
 * `split(': ')` の結果へ `as [string, ...string[]]` を当てると（AGENTS.md 1 違反であるうえ）
 * 区切りが無い文字列を渡したときに列名へ本文全体が入る誤りに気づけない。
 * ここで**区切りの有無を明示的に分岐**させ、両方の経路をテストで固定する。
 */
export const splitFieldMessage = (errorMessage: string): { field: string; message: string } => {
  const separatorIndex = errorMessage.indexOf(': ');
  if (separatorIndex < 0) {
    // ヘッダーエラーなど列名を持たないメッセージ（`errors` は同じ配列に混在し得る）
    return { field: '', message: errorMessage };
  }
  return {
    field: errorMessage.slice(0, separatorIndex),
    message: errorMessage.slice(separatorIndex + ': '.length),
  };
};

/**
 * バリデーションエラーをユーザーフレンドリーなメッセージに変換
 */
const formatValidationErrors = (error: z.ZodError): string[] => {
  return error.issues.map((err: z.ZodIssue) => {
    const field = err.path.join('.');
    return `${field}: ${err.message}`;
  });
};

/**
 * 汎用バッチバリデーション
 */
const validateEntities = <T>(
  schema: z.ZodSchema<T>,
  rows: unknown[]
): {
  valid: T[];
  invalid: Array<{ row: number; data: unknown; errors: string[] }>;
} => {
  const valid: T[] = [];
  const invalid: Array<{ row: number; data: unknown; errors: string[] }> = [];

  rows.forEach((row, index) => {
    const invalidLiteralFields = findInvalidLiteralFields(row);
    if (invalidLiteralFields.length > 0) {
      invalid.push({
        row: index + 1,
        data: row,
        errors: invalidLiteralFields.map(
          (field) =>
            `${field}: 'undefined' / 'null' は値として使用できません（データ書き出し時の欠損値の可能性があります）`
        ),
      });
      return;
    }
    const result = validateEntity(schema, row);
    if (result.success) {
      valid.push(result.data);
    } else {
      invalid.push({
        row: index + 1,
        data: row,
        errors: formatValidationErrors(result.errors),
      });
    }
  });

  return { valid, invalid };
};

/**
 * アップロードタイプに応じたバッチバリデーション
 */
export const validateByUploadType = (uploadType: string, rows: unknown[]) => {
  const entityType = getEntityType(uploadType);
  const config = ENTITY_CONFIGS[entityType];
  // UPLOAD_TYPE_MAP と ENTITY_CONFIGS のキーが食い違ったときの保険。
  // 現状は getEntityType が先に throw するため到達しないが、片方だけに
  // エントリを足した場合の早期検知として残す。
  /* v8 ignore next 3 -- 到達不能な防御的分岐（上記コメント参照） */
  if (!config) {
    throw new Error(`Unknown upload type: ${uploadType}`);
  }
  const result = validateEntities(config.schema, rows);

  // クロスフィールドバリデーション
  const crossFieldValidation = config.crossFieldValidation;
  if (crossFieldValidation) {
    const validatedRows = [...result.valid];
    result.valid = [];
    validatedRows.forEach((row, index) => {
      // `EntityConfig.schema` は `z.ZodObject` なので検証済みの行は必ずオブジェクトであり、
      // `Record<string, unknown>` へそのまま渡せる（旧実装の `as` は不要だった）。
      // `!`（Non-null Assertion）もローカル変数への退避で不要になった。
      const crossErrors = crossFieldValidation(row, index);
      if (crossErrors.length > 0) {
        result.invalid.push({ row: index + 1, data: row, errors: crossErrors });
      } else {
        result.valid.push(row);
      }
    });
  }

  return result;
};

/**
 * Book専用バリデーション（後方互換性）
 */
export const validateBooks = (rows: unknown[]) => validateEntities(BookCsvUploadSchema, rows);

/**
 * CSV配列データをオブジェクトに変換（汎用）
 */
export const csvRowsToObjects = (
  headers: string[],
  rows: string[][],
  entityType: keyof typeof ENTITY_CONFIGS = 'book'
): Record<string, unknown>[] => {
  const config = ENTITY_CONFIGS[entityType];
  if (!config) {
    throw new Error(`Unknown entity type: ${entityType}`);
  }

  const { optionalFields, numberFields, booleanFields } = config;

  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    headers.forEach((header, index) => {
      const value = row[index]?.trim() || '';
      const fieldName = header;

      // 空文字列の場合
      if (value === '') {
        // Optional フィールドは undefined にする（pattern チェックをスキップ）
        if (
          optionalFields.includes(fieldName) ||
          numberFields.includes(fieldName) ||
          booleanFields.includes(fieldName)
        ) {
          obj[fieldName] = undefined;
        } else {
          // Required フィールドは空文字列のまま（required チェックが効く）
          obj[fieldName] = '';
        }
      } else {
        // 値がある場合
        if (numberFields.includes(fieldName)) {
          obj[fieldName] = Number(value);
        } else if (booleanFields.includes(fieldName)) {
          obj[fieldName] = value === 'true' || value === '1';
        } else {
          obj[fieldName] = value;
        }
      }
    });
    return obj;
  });
};
